import type { WaterSection, WaterStation } from "@weather-deck/shared";
import { EISBACH_STATION_ID, ISAR_MUNICH_STATION_ID, isCurrentWaterSection } from "@weather-deck/shared";
import { el } from "./dom";

/** Swim-relevant banding for water temperature. Deliberately coarse. */
export function tempBand(c: number): { label: string; className: string } {
  if (c >= 20) return { label: "warm", className: "is-warm" };
  if (c >= 16) return { label: "fresh", className: "is-fresh" };
  if (c >= 10) return { label: "cold", className: "is-cold" };
  return { label: "very cold", className: "is-icy" };
}

/**
 * How far today's flow sits from this gauge's own long-term mean.
 *
 * Coarse on purpose, and asymmetric: the interesting question on a Bavarian
 * river in summer is how little water there is, so the dry end gets three bands
 * and the wet end two. The thresholds are read as "a third", "half", "normal",
 * "half again" — not as anything the hydrologists define.
 */
export function flowBand(ratio: number): { label: string; className: string } {
  if (ratio < 0.4) return { label: "very low", className: "is-verylow" };
  if (ratio < 0.7) return { label: "low", className: "is-low" };
  if (ratio <= 1.4) return { label: "normal", className: "is-normal" };
  if (ratio <= 2) return { label: "high", className: "is-high" };
  return { label: "very high", className: "is-veryhigh" };
}

/** A ratio as a percentage, rounded the way a reader would say it aloud. */
export function fmtRatio(ratio: number): string {
  return `${ratio < 0.1 ? Math.round(ratio * 1000) / 10 : Math.round(ratio * 100)}%`;
}

export function fmtAge(measuredAt: string | null, now: Date): string {
  if (!measuredAt) return "";
  const mins = Math.round((now.getTime() - new Date(measuredAt).getTime()) / 60_000);
  if (!Number.isFinite(mins) || mins < 0) return "";
  if (mins < 90) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 36) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/**
 * Still water or running water, from the water body's own name.
 *
 * German makes this unusually reliable: standing water carries See, Weiher,
 * Teich or Stausee in the name (Tegernsee, Schliersee, Ammersee, Starnberger
 * See), and Bavarian rivers — Isar, Würm, Amper, Loisach, Eisbach — do not. It
 * is still a naming heuristic rather than a field the service publishes, so it
 * is kept in one place and named honestly.
 */
export function isStillWater(waterName: string): boolean {
  const n = waterName.trim();
  const words = "see|weiher|teich|stausee|speicher";
  // Two forms, because German does both: a separate word ("Starnberger See")
  // and a compound suffix ("Tegernsee", "Sylvensteinspeicher"). A word-boundary
  // match alone misses the compounds, since there is no boundary inside one.
  return new RegExp(`(^|\\s)(${words})(\\s|$)`, "i").test(n)
      || new RegExp(`(${words})$`, "i").test(n);
}

export type WaterGroup = { water: string; stations: WaterStation[] };

/**
 * Collapse stations onto their water body — the Isar carries several gauges,
 * and five separate "Isar" headings would read as five rivers.
 *
 * Order is the order the stations arrive in, which the worker has already set:
 * watch-list order between waters, and the service's own order within one
 * water, which runs upstream to downstream.
 */
export function groupByWater(stations: WaterStation[]): WaterGroup[] {
  const byWater = new Map<string, WaterGroup>();
  for (const s of stations) {
    const key = s.water.trim().toUpperCase();
    const found = byWater.get(key);
    if (found) found.stations.push(s);
    else byWater.set(key, { water: s.water.trim(), stations: [s] });
  }
  return [...byWater.values()];
}

function metric(label: string, value: string, sub?: string): HTMLElement {
  return el("div", { className: "water-metric" }, [
    el("span", { className: "water-metric-label", text: label }),
    el("span", { className: "water-metric-value", text: value }),
    sub ? el("span", { className: "water-metric-sub", text: sub }) : document.createTextNode("")
  ]);
}

/**
 * Level and temperature come from two different services read at two different
 * minutes, so one shared "15 min ago" would misdate one of them. They only get
 * separate labels when they actually disagree.
 */
function ageLine(s: WaterStation, now: Date): string {
  const gauge = fmtAge(s.measuredAt, now);
  const temp = fmtAge(s.temperatureAt, now);
  if (gauge && temp && gauge !== temp) return `gauge ${gauge} · temp ${temp}`;
  return gauge || temp;
}

function buildStationRow(s: WaterStation, now: Date, showName: boolean): HTMLElement {
  const row = el("div", { className: "water-station" });

  if (showName) row.append(el("div", { className: "water-station-name", text: s.name }));

  const metrics = el("div", { className: "water-metrics" });
  if (s.temperatureC != null) {
    const band = tempBand(s.temperatureC);
    const m = metric("Temp", `${s.temperatureC.toFixed(1)}°C`, band.label);
    m.classList.add(band.className);
    metrics.append(m);
  }
  if (s.levelCm != null) metrics.append(metric("Gauge", `${Math.round(s.levelCm)} cm`, "above zero"));
  if (s.dischargeCms != null) metrics.append(metric("Flow", `${s.dischargeCms.toFixed(1)}`, "m³/s"));
  // Only discharge gets a "vs normal": a gauge level is measured above the
  // station's own arbitrary zero, so a percentage of it would be meaningless.
  const f = s.flowVsNormal;
  if (f) {
    const band = flowBand(f.ratio);
    const m = metric("vs normal", fmtRatio(f.ratio), `${band.label} · MQ ${f.meanCms} m³/s`);
    m.classList.add(band.className);
    metrics.append(m);
  }
  row.append(metrics);

  if (s.floodStage != null) {
    row.append(el("div", { className: "water-stage", text: `Meldestufe ${s.floodStage}` }));
  }
  // MNQ is the mean of the annual lows, so at or under it this is a low even by
  // the standards of the river's own low-water season — worth saying outright
  // rather than leaving the reader to judge a percentage.
  if (f?.atOrBelowTypicalLow) {
    row.append(el("div", {
      className: "water-lowflow",
      text: `at or below the typical ${f.season === "sommer" ? "summer" : "winter"} low (MNQ ${f.lowMeanCms} m³/s)`
    }));
  }
  // HND flags a gauge it does not trust. Showing the number without the flag
  // would pass off a known-bad reading as a good one.
  if (s.outage) row.append(el("div", { className: "water-outage", text: s.outage }));

  const age = ageLine(s, now);
  if (age) row.append(el("div", { className: "water-age", text: age }));
  return row;
}

/**
 * Lake, creek or river, from the water body's own name.
 *
 * A third category on top of isStillWater, because "Rivers (7)" reads oddly for
 * the Eisbach and the Schwabinger Bach — both are narrow city channels, and both
 * say so in their names. German is reliable here for the same reason the lakes
 * are: -bach is a creek and nothing else is.
 *
 * Still a naming heuristic rather than a field the service publishes, so it is
 * kept beside isStillWater and named honestly. It only decides which heading a
 * water is filed under; nothing numeric depends on it.
 */
export type WaterKind = "lake" | "creek" | "river";

export function waterKind(waterName: string): WaterKind {
  if (isStillWater(waterName)) return "lake";
  return /bach$/i.test(waterName.trim()) ? "creek" : "river";
}

/**
 * The temperature to show on a water body's closed row.
 *
 * Craig's ask: see the temperature without opening the water. A single station
 * gives one number; several give the range across them, because the Isar spans
 * 10.1 °C at Mittenwald and 21.8 °C at Landau on the same afternoon and one of
 * those printed alone would be a fiction about the other.
 */
export function groupTemperatureLabel(g: WaterGroup): string {
  const temps = g.stations.map((s) => s.temperatureC).filter((t): t is number => t != null);
  if (temps.length === 0) return "";
  const lo = Math.min(...temps);
  const hi = Math.max(...temps);
  // An en dash, not a hyphen: this is a range, and "10.1-19.6" reads as a sum
  // that has gone wrong.
  return lo === hi ? `${lo.toFixed(1)}°C` : `${lo.toFixed(1)}–${hi.toFixed(1)}°C`;
}

/**
 * One representative flow-against-normal for a water body, as a median.
 *
 * The median and not the range, and not the mean, because Bavarian rivers are
 * heavily diverted and their gauges genuinely disagree. Read 21.08.2026: the
 * Isar is at 30% of its summer mean in München but 81% at Freising, because the
 * Mittlere-Isar-Kanal takes water out in between; the Mangfall runs 102% at
 * Valley and 12% at Bad Aibling. A range would put "12%–102%" on the card,
 * which reads as a broken number rather than as a diverted river, and a mean
 * would let one regulated gauge drag the whole water.
 *
 * The spread is not hidden — every gauge shows its own figure one click down,
 * and any gauge under MNQ raises the low-water flag regardless of the median.
 *
 * Lakes return null: they publish no discharge at all.
 */
export function groupFlowRatio(g: WaterGroup): number | null {
  const ratios = g.stations
    .map((s) => s.flowVsNormal?.ratio)
    .filter((r): r is number => r != null)
    .sort((a, b) => a - b);
  if (ratios.length === 0) return null;
  const mid = Math.floor(ratios.length / 2);
  return ratios.length % 2 ? ratios[mid]! : (ratios[mid - 1]! + ratios[mid]!) / 2;
}

export function groupFlowLabel(g: WaterGroup): string {
  const r = groupFlowRatio(g);
  return r == null ? "" : `${fmtRatio(r)} of normal`;
}

/**
 * Is any gauge on this water at or below its mean annual low?
 *
 * Kept separate from the median on purpose: a river can sit near its long-term
 * mean overall while one reach is at record low, and that reach is the news.
 */
export function groupHasLowWater(g: WaterGroup): boolean {
  return g.stations.some((s) => s.flowVsNormal?.atOrBelowTypicalLow === true);
}

function buildGroup(g: WaterGroup, now: Date): HTMLElement {
  const block = el("details", { className: "water-group" });
  const count = `${g.stations.length} ${g.stations.length === 1 ? "gauge" : "gauges"}`;
  const temp = groupTemperatureLabel(g);
  const flow = groupFlowLabel(g);
  const flowRatio = groupFlowRatio(g);
  const summary = el("summary", { className: "water-body" }, [
    el("span", { className: "water-body-name", text: g.water }),
    el("span", { className: "water-body-temp", text: temp }),
    el("span", {
      className: "water-body-meta",
      text: g.stations.length === 1 ? `${count} · ${g.stations[0]!.name}` : `${count}, upstream first`
    })
  ]);
  if (flow && flowRatio != null) {
    const badge = el("span", { className: "water-body-flow", text: flow });
    badge.classList.add(flowBand(flowRatio).className);
    summary.append(badge);
  }
  // Independent of the median: one reach at its record low is the news even
  // when the river as a whole reads normal.
  if (groupHasLowWater(g)) {
    summary.append(el("span", { className: "water-body-lowwater", text: "low water" }));
  }
  block.append(summary);
  const multi = g.stations.length > 1;
  const rows = el("div", { className: "water-group-rows" });
  for (const s of g.stations) rows.append(buildStationRow(s, now, multi));
  block.append(rows);
  return block;
}

/** German collation, so Würm sorts after Wörthsee rather than after Z. */
export function sortWatersAlphabetically(groups: WaterGroup[]): WaterGroup[] {
  return [...groups].sort((a, b) => a.water.localeCompare(b.water, "de"));
}

function buildSubsection(title: string, groups: WaterGroup[], now: Date): HTMLElement | null {
  if (groups.length === 0) return null;
  const wrap = el("div", { className: "water-subsection" });
  wrap.append(el("h4", { className: "water-subhead", text: `${title} (${groups.length})` }));
  const list = el("div", { className: "water-list" });
  // Alphabetical inside a category, not the worker's upstream-first ranking:
  // that ranking orders *gauges within* a river and is kept there, but across
  // seventeen waters the only order a reader can predict is A–Z.
  for (const g of sortWatersAlphabetically(groups)) list.append(buildGroup(g, now));
  wrap.append(list);
  return wrap;
}

/**
 * The Eisbach against the Isar that feeds it.
 *
 * Why this block exists: Telekom's AI datacentre under Tucherpark draws Eisbach
 * water through a heat exchanger and returns it "a few degrees warmer"
 * (t-online, muenchen.t-online.de/region/muenchen/id_101113796). The obvious
 * question is whether that shows up in the water — and the honest answer, which
 * this block states rather than hides, is that the public network cannot see
 * it. There is exactly one Eisbach gauge, München Himmelreichbrücke, and it
 * sits in the southern Englischer Garten roughly 1 km *upstream* of Tucherpark
 * (GKD master data puts it at 48.1467 N, 11.5909 E, 506 m; the Eisbach's course
 * runs Haus der Kunst → Englischer Garten → Tucherpark → Tivoli → Isar). It
 * measures the water going in, not the water coming out.
 *
 * What the pair does show is real and worth watching: the Eisbach is a shallow
 * 2.8 km side channel of the Isar, so the difference between the two is how
 * much the city warms that water on the way through — before the datacentre
 * adds anything.
 */
export function buildEisbachWatch(stations: WaterStation[]): HTMLElement | null {
  const eisbach = stations.find((s) => s.id === EISBACH_STATION_ID);
  const isar = stations.find((s) => s.id === ISAR_MUNICH_STATION_ID);
  if (!eisbach) return null;

  const delta = eisbach.temperatureC != null && isar?.temperatureC != null
    ? eisbach.temperatureC - isar.temperatureC
    : null;

  const headline = eisbach.temperatureC != null
    ? `Eisbach ${eisbach.temperatureC.toFixed(1)}°C` +
      (delta != null ? ` · ${delta >= 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)} K vs the Isar` : "")
    : "Eisbach — no temperature reading";

  const box = el("details", { className: "eisbach-watch" });
  box.append(el("summary", {}, [
    el("span", { className: "eisbach-title", text: "Eisbach & the Tucherpark datacentre" }),
    el("span", { className: "eisbach-headline", text: headline })
  ]));

  const rows = el("div", { className: "eisbach-rows" });
  const line = (label: string, s: WaterStation | undefined): HTMLElement => {
    const parts: string[] = [];
    if (s?.temperatureC != null) parts.push(`${s.temperatureC.toFixed(1)}°C`);
    if (s?.dischargeCms != null) parts.push(`${s.dischargeCms.toFixed(1)} m³/s`);
    return el("div", { className: "eisbach-row" }, [
      el("span", { className: "eisbach-row-label", text: label }),
      el("span", { className: "eisbach-row-value", text: parts.length ? parts.join(" · ") : "no reading" })
    ]);
  };
  rows.append(line("Eisbach, Himmelreichbrücke", eisbach));
  rows.append(line("Isar, München", isar));
  if (delta != null) {
    // Kelvin, not °C: this is a temperature *difference*, and "1.8 °C" would
    // read as a temperature.
    rows.append(el("div", { className: "eisbach-row is-delta" }, [
      el("span", { className: "eisbach-row-label", text: "Difference" }),
      el("span", {
        className: "eisbach-row-value",
        text: `${delta >= 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)} K`
      })
    ]));
  }
  box.append(rows);

  box.append(el("p", { className: "eisbach-note", text:
    "The datacentre under Tucherpark cools its chips with Eisbach water through a heat " +
    "exchanger and returns it a few degrees warmer. This gauge cannot measure that: " +
    "München Himmelreichbrücke is the only public sensor on the Eisbach and it sits about " +
    "a kilometre upstream of Tucherpark, so it reads the water going in. There is no " +
    "downstream gauge to pair it with."
  }));
  box.append(el("p", { className: "eisbach-note", text:
    "What the difference above does show is how much warmer the Eisbach runs than the Isar " +
    "that feeds it — a shallow 2.8 km channel through the city gains heat from sun and air " +
    "on its own. A positive number is not evidence of the datacentre."
  }));
  return box;
}

/**
 * Rivers and lakes from the Bavarian state gauge network.
 *
 * Renders nothing at all when the section is absent — a snapshot written before
 * water support, or a run where both feeds failed — rather than showing an
 * empty shell. An empty *station list* is different and does get a message: the
 * feeds answered and genuinely carried nothing.
 */
export function buildWaterCard(water: WaterSection | undefined, now: Date = new Date()): HTMLElement {
  const container = el("section", { className: "water-card" });
  // Absent, or a section left over from before the Bavarian-state-services
  // migration. The
  // fields below are not in the old shape, and rendering what *is* there would
  // put a stale, plausible-looking card on screen while the live lookup is
  // failing — the one outcome worse than showing nothing.
  if (!isCurrentWaterSection(water)) return container;

  if (water.stations.length === 0) {
    container.append(el("h3", { text: "Rivers & lakes" }));
    container.append(el("p", {
      className: "water-empty",
      text: "No gauge readings came back for the Munich waters — the Isar, Eisbach, Würm and " +
            "Amper, and the lakes out to Tegernsee and Schliersee. HND Bayern and GKD Bayern " +
            "answered but carried nothing for them."
    }));
    return container;
  }

  // The Eisbach block sits outside the collapsed list and stays open on the
  // deck: it is the one reading here worth seeing without a click, and burying
  // it behind the other 47 gauges would defeat the point of having it.
  const eisbach = buildEisbachWatch(water.stations);
  if (eisbach) container.append(eisbach);

  const groups = groupByWater(water.stations);
  const creeks = groups.filter((g) => waterKind(g.water) === "creek");
  const rivers = groups.filter((g) => waterKind(g.water) === "river");
  const lakes = groups.filter((g) => waterKind(g.water) === "lake");

  // Forty-eight gauges across seventeen waters is more than a deck can show at
  // once, so the whole network collapses to one line and opens on demand. Two
  // levels of <details>, matching the daily forecast: the card, then each water.
  const box = el("details", { className: "water-browser" });
  const gauges = `${water.stations.length} ${water.stations.length === 1 ? "gauge" : "gauges"}`;
  box.append(el("summary", { className: "water-browser-summary" }, [
    el("span", { className: "water-browser-title", text: "Rivers & lakes" }),
    el("span", {
      className: "water-browser-meta",
      text: `${gauges} · ${groups.length} waters`
    })
  ]));

  for (const [title, gs] of [["Creeks", creeks], ["Rivers", rivers], ["Lakes", lakes]] as const) {
    const block = buildSubsection(title, gs, now);
    if (block) box.append(block);
  }

  // Say what is missing, not only what is present — a lakes section that is
  // simply absent reads as "there are no lakes", which is not the finding.
  if (lakes.length === 0) {
    box.append(el("p", { className: "water-empty", text: "No lake gauges came back this run." }));
  }
  if (!water.sources.temperature) {
    box.append(el("p", {
      className: "water-empty",
      text: "Water temperatures are missing: GKD Bayern did not answer this run. Levels and " +
            "flow above are current."
    }));
  }
  if (!water.sources.level) {
    box.append(el("p", {
      className: "water-empty",
      text: "Levels and flow are missing: HND Bayern did not answer this run. Temperatures " +
            "above are current."
    }));
  }

  box.append(el("p", {
    className: "water-note",
    text: "Level and flow from HND Bayern, water temperature and long-term means from GKD " +
          "Bayern (CC BY 4.0). Gauge is level above the station's own zero, not absolute " +
          "depth. Flow is discharge — the volume passing per second. Gauges do not publish a " +
          "speed in m/s: that needs the channel cross-section, which is not public."
  }));
  box.append(el("p", {
    className: "water-note",
    text: "“Of normal” compares flow with MQ, this gauge's own mean discharge for the " +
          "current hydrological half-year (summer 01.05.–31.10., winter 01.11.–30.04.). Each " +
          "station has its own reference period, some starting in 1926 and some in 1985, so " +
          "the figures are not comparable between stations. There is no equivalent for gauge " +
          "level: it is measured above each station's own zero, so a percentage of it would " +
          "mean nothing — 92 cm on the Isar is 24 m³/s in München and 69 m³/s at Landshut."
  }));
  container.append(box);
  return container;
}
